import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { scheduleAdjustmentAPI, timeAdjustmentAPI, shiftAPI, attendanceAPI, shiftChangeAPI, otRequestAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Calendar, Clock, RefreshCw, FileEdit, Plus, AlertTriangle, Timer } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';

const MyRequestsPage = () => {
  const { user } = useAuth();
  const companyId = user?.company_id;
  
  const [activeTab, setActiveTab] = useState('shift_change');
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [attendance, setAttendance] = useState([]);
  
  // Request data
  const [shiftChangeRequests, setShiftChangeRequests] = useState([]);
  const [scheduleRequests, setScheduleRequests] = useState([]);
  const [timeRequests, setTimeRequests] = useState([]);
  const [otRequests, setOtRequests] = useState([]);
  
  // Dialog states
  const [shiftChangeDialogOpen, setShiftChangeDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [otDialogOpen, setOtDialogOpen] = useState(false);
  
  // Form states
  const [shiftChangeForm, setShiftChangeForm] = useState({
    date: '',
    requested_shift_template_id: '',
    reason: ''
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    adjustment_type: 'temporary',
    start_date: '',
    end_date: '',
    is_recurring: false,
    recurring_days: [],
    requested_shift_template_id: '',
    reason: ''
  });
  
  const [timeForm, setTimeForm] = useState({
    attendance_id: '',
    date: '',
    proposed_clock_in: '',
    proposed_clock_out: '',
    reason: ''
  });
  
  const [otForm, setOtForm] = useState({
    date: '',
    requested_hours: '',
    start_time: '',
    end_time: '',
    reason: '',
    project_code: ''
  });

  const loadBaseData = useCallback(async () => {
    if (!companyId || !user?.id) return;
    try {
      const [shiftsRes, attendanceRes] = await Promise.all([
        shiftAPI.getAll(companyId),
        attendanceAPI.getAll(companyId, null, user.id)
      ]);
      setShifts(shiftsRes.data);
      setAttendance(attendanceRes.data.slice(0, 30)); // Last 30 records
    } catch (error) {
      console.error('Error loading base data:', error);
    }
  }, [companyId, user?.id]);

  const loadRequests = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [shiftRes, scheduleRes, timeRes, otRes] = await Promise.all([
        shiftChangeAPI.getAll(companyId).catch(() => ({ data: [] })),
        scheduleAdjustmentAPI.getMine().catch(() => ({ data: [] })),
        timeAdjustmentAPI.getMine().catch(() => ({ data: [] })),
        otRequestAPI.getMine().catch(() => ({ data: [] }))
      ]);
      setShiftChangeRequests(shiftRes.data.filter(r => r.employee_id === user?.id) || []);
      setScheduleRequests(scheduleRes.data || []);
      setTimeRequests(timeRes.data || []);
      setOtRequests(otRes.data || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, user?.id]);

  useEffect(() => {
    loadBaseData();
    loadRequests();
  }, [loadBaseData, loadRequests]);

  const handleOtSubmit = async () => {
    if (!otForm.date || !otForm.requested_hours || !otForm.reason) {
      toast.error('Please fill in date, hours, and reason');
      return;
    }
    
    try {
      await otRequestAPI.create({
        date: otForm.date,
        requested_hours: parseFloat(otForm.requested_hours),
        start_time: otForm.start_time || null,
        end_time: otForm.end_time || null,
        reason: otForm.reason,
        project_code: otForm.project_code || null
      });
      toast.success('OT request submitted for approval');
      setOtDialogOpen(false);
      setOtForm({ date: '', requested_hours: '', start_time: '', end_time: '', reason: '', project_code: '' });
      loadRequests();
    } catch (error) {
      console.error('Error submitting OT request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit OT request');
    }
  };

  const handleShiftChangeSubmit = async () => {
    if (!shiftChangeForm.date || !shiftChangeForm.requested_shift_template_id || !shiftChangeForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await shiftChangeAPI.create(shiftChangeForm);
      toast.success('Shift change request submitted');
      setShiftChangeDialogOpen(false);
      setShiftChangeForm({ date: '', requested_shift_template_id: '', reason: '' });
      loadRequests();
    } catch (error) {
      console.error('Error submitting shift change:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleScheduleSubmit = async () => {
    if (!scheduleForm.start_date || !scheduleForm.requested_shift_template_id || !scheduleForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await scheduleAdjustmentAPI.create(scheduleForm);
      toast.success('Schedule adjustment request submitted');
      setScheduleDialogOpen(false);
      setScheduleForm({
        adjustment_type: 'temporary',
        start_date: '',
        end_date: '',
        is_recurring: false,
        recurring_days: [],
        requested_shift_template_id: '',
        reason: ''
      });
      loadRequests();
    } catch (error) {
      console.error('Error submitting schedule adjustment:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleTimeSubmit = async () => {
    if (!timeForm.attendance_id || !timeForm.reason) {
      toast.error('Please select an attendance record and provide a reason');
      return;
    }
    
    if (!timeForm.proposed_clock_in && !timeForm.proposed_clock_out) {
      toast.error('Please provide at least one corrected time');
      return;
    }
    
    try {
      await timeAdjustmentAPI.create(timeForm);
      toast.success('Time adjustment request submitted');
      setTimeDialogOpen(false);
      setTimeForm({
        attendance_id: '',
        date: '',
        proposed_clock_in: '',
        proposed_clock_out: '',
        reason: ''
      });
      loadRequests();
    } catch (error) {
      console.error('Error submitting time adjustment:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      manager_approved: 'outline',
      approved: 'default',
      rejected: 'destructive',
      cancelled: 'outline'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status?.replace('_', ' ')}</Badge>;
  };

  const toggleRecurringDay = (day) => {
    setScheduleForm(prev => ({
      ...prev,
      recurring_days: prev.recurring_days.includes(day)
        ? prev.recurring_days.filter(d => d !== day)
        : [...prev.recurring_days, day]
    }));
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const selectAttendanceForAdjustment = (att) => {
    setTimeForm({
      ...timeForm,
      attendance_id: att.id,
      date: att.date,
      proposed_clock_in: att.clock_in ? format(new Date(att.clock_in), "yyyy-MM-dd'T'HH:mm") : '',
      proposed_clock_out: att.clock_out ? format(new Date(att.clock_out), "yyyy-MM-dd'T'HH:mm") : ''
    });
  };

  return (
    <div className="space-y-6" data-testid="my-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Requests</h1>
          <p className="text-muted-foreground">Submit shift changes, schedule adjustments, time corrections, and OT requests</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="shift_change" className="gap-2">
            <Calendar className="h-4 w-4" />
            Shift Change
          </TabsTrigger>
          <TabsTrigger value="schedule" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="time" className="gap-2">
            <Clock className="h-4 w-4" />
            Time Adjust
          </TabsTrigger>
          <TabsTrigger value="overtime" className="gap-2" data-testid="ot-tab">
            <Timer className="h-4 w-4" />
            OT Request
          </TabsTrigger>
        </TabsList>

        {/* Shift Change Tab */}
        <TabsContent value="shift_change" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Shift Change Requests</CardTitle>
                <CardDescription>Request a different shift for a specific day</CardDescription>
              </div>
              <Dialog open={shiftChangeDialogOpen} onOpenChange={setShiftChangeDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="new-shift-change-btn">
                    <Plus className="h-4 w-4" />
                    New Request
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Request Shift Change</DialogTitle>
                    <DialogDescription>Request a different shift for one day</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input 
                        type="date"
                        value={shiftChangeForm.date}
                        onChange={(e) => setShiftChangeForm({...shiftChangeForm, date: e.target.value})}
                        min={format(new Date(), 'yyyy-MM-dd')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Requested Shift *</Label>
                      <Select 
                        value={shiftChangeForm.requested_shift_template_id}
                        onValueChange={(value) => setShiftChangeForm({...shiftChangeForm, requested_shift_template_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select shift" />
                        </SelectTrigger>
                        <SelectContent>
                          {shifts.map(shift => (
                            <SelectItem key={shift.id} value={shift.id}>
                              {shift.name} ({shift.start_time} - {shift.end_time})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Reason *</Label>
                      <Textarea 
                        value={shiftChangeForm.reason}
                        onChange={(e) => setShiftChangeForm({...shiftChangeForm, reason: e.target.value})}
                        placeholder="Why do you need this shift change?"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShiftChangeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleShiftChangeSubmit}>Submit Request</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Requested Shift</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shiftChangeRequests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell>{req.date}</TableCell>
                      <TableCell>
                        {shifts.find(s => s.id === req.requested_shift_template_id)?.name || 'Unknown'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{req.reason}</TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {req.created_at ? format(new Date(req.created_at), 'MMM d, h:mm a') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {shiftChangeRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No shift change requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Adjustment Tab */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Schedule Adjustment Requests</CardTitle>
                <CardDescription>Request temporary or permanent schedule changes</CardDescription>
              </div>
              <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="new-schedule-adj-btn">
                    <Plus className="h-4 w-4" />
                    New Request
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Request Schedule Adjustment</DialogTitle>
                    <DialogDescription>
                      Temporary changes need manager approval. Permanent changes need manager + HR approval.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Adjustment Type *</Label>
                      <Select 
                        value={scheduleForm.adjustment_type}
                        onValueChange={(value) => setScheduleForm({...scheduleForm, adjustment_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="temporary">Temporary (Manager approval)</SelectItem>
                          <SelectItem value="permanent">Permanent (Manager + HR approval)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input 
                          type="date"
                          value={scheduleForm.start_date}
                          onChange={(e) => setScheduleForm({...scheduleForm, start_date: e.target.value})}
                        />
                      </div>
                      {scheduleForm.adjustment_type === 'temporary' && (
                        <div className="space-y-2">
                          <Label>End Date</Label>
                          <Input 
                            type="date"
                            value={scheduleForm.end_date}
                            onChange={(e) => setScheduleForm({...scheduleForm, end_date: e.target.value})}
                          />
                        </div>
                      )}
                    </div>
                    
                    {scheduleForm.adjustment_type === 'temporary' && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={scheduleForm.is_recurring}
                            onCheckedChange={(checked) => setScheduleForm({...scheduleForm, is_recurring: checked})}
                          />
                          <Label>Recurring on specific days</Label>
                        </div>
                        {scheduleForm.is_recurring && (
                          <div className="flex gap-2 mt-2">
                            {dayNames.map((day, idx) => (
                              <Button
                                key={day}
                                variant={scheduleForm.recurring_days.includes(idx) ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => toggleRecurringDay(idx)}
                              >
                                {day}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Requested Shift *</Label>
                      <Select 
                        value={scheduleForm.requested_shift_template_id}
                        onValueChange={(value) => setScheduleForm({...scheduleForm, requested_shift_template_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select shift" />
                        </SelectTrigger>
                        <SelectContent>
                          {shifts.map(shift => (
                            <SelectItem key={shift.id} value={shift.id}>
                              {shift.name} ({shift.start_time} - {shift.end_time})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Reason *</Label>
                      <Textarea 
                        value={scheduleForm.reason}
                        onChange={(e) => setScheduleForm({...scheduleForm, reason: e.target.value})}
                        placeholder="Why do you need this schedule change?"
                      />
                    </div>
                    
                    {scheduleForm.adjustment_type === 'permanent' && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-yellow-800">
                          Permanent schedule changes require both manager and HR/Admin approval.
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleScheduleSubmit}>Submit Request</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Date Range</TableHead>
                    <TableHead>Requested Shift</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduleRequests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell>
                        <Badge variant={req.adjustment_type === 'permanent' ? 'default' : 'outline'}>
                          {req.adjustment_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {req.start_date}{req.end_date ? ` to ${req.end_date}` : ''}
                        {req.is_recurring && <Badge variant="secondary" className="ml-1">Recurring</Badge>}
                      </TableCell>
                      <TableCell>
                        {shifts.find(s => s.id === req.requested_shift_template_id)?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {req.created_at ? format(new Date(req.created_at), 'MMM d, h:mm a') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {scheduleRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No schedule adjustment requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Adjustment Tab */}
        <TabsContent value="time" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Time Adjustment Requests</CardTitle>
                <CardDescription>Request corrections to your clock-in/out times</CardDescription>
              </div>
              <Dialog open={timeDialogOpen} onOpenChange={setTimeDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="new-time-adj-btn">
                    <Plus className="h-4 w-4" />
                    New Request
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Request Time Adjustment</DialogTitle>
                    <DialogDescription>Select an attendance record and provide the correct times</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Select Attendance Record *</Label>
                      <ScrollArea className="h-[200px] border rounded-md p-2">
                        <div className="space-y-2">
                          {attendance.map(att => (
                            <div 
                              key={att.id}
                              className={`p-3 rounded-lg cursor-pointer border ${timeForm.attendance_id === att.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                              onClick={() => selectAttendanceForAdjustment(att)}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{att.date}</span>
                                <Badge variant="outline">{att.status}</Badge>
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                In: {att.clock_in ? format(new Date(att.clock_in), 'h:mm a') : 'N/A'} | 
                                Out: {att.clock_out ? format(new Date(att.clock_out), 'h:mm a') : 'N/A'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                    
                    {timeForm.attendance_id && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Correct Clock-In Time</Label>
                            <Input 
                              type="datetime-local"
                              value={timeForm.proposed_clock_in}
                              onChange={(e) => setTimeForm({...timeForm, proposed_clock_in: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Correct Clock-Out Time</Label>
                            <Input 
                              type="datetime-local"
                              value={timeForm.proposed_clock_out}
                              onChange={(e) => setTimeForm({...timeForm, proposed_clock_out: e.target.value})}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Reason *</Label>
                          <Textarea 
                            value={timeForm.reason}
                            onChange={(e) => setTimeForm({...timeForm, reason: e.target.value})}
                            placeholder="Why does this time need adjustment? (e.g., forgot to clock in, system error)"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setTimeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleTimeSubmit} disabled={!timeForm.attendance_id}>Submit Request</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Original Times</TableHead>
                    <TableHead>Proposed Times</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeRequests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell>{req.date}</TableCell>
                      <TableCell className="text-sm">
                        <div>In: {req.original_clock_in ? format(new Date(req.original_clock_in), 'h:mm a') : 'N/A'}</div>
                        <div>Out: {req.original_clock_out ? format(new Date(req.original_clock_out), 'h:mm a') : 'N/A'}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="text-green-600">In: {req.proposed_clock_in ? format(new Date(req.proposed_clock_in), 'h:mm a') : '-'}</div>
                        <div className="text-green-600">Out: {req.proposed_clock_out ? format(new Date(req.proposed_clock_out), 'h:mm a') : '-'}</div>
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">{req.reason}</TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                    </TableRow>
                  ))}
                  {timeRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No time adjustment requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OT Request Tab */}
        <TabsContent value="overtime" className="space-y-4" data-testid="ot-requests-tab">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Overtime Requests</CardTitle>
                <CardDescription>Request pre-approval for overtime work. OT must be approved before it&apos;s included in payroll.</CardDescription>
              </div>
              <Dialog open={otDialogOpen} onOpenChange={setOtDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="new-ot-request-btn">
                    <Plus className="h-4 w-4" />
                    New OT Request
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Request Overtime</DialogTitle>
                    <DialogDescription>
                      Submit a request for overtime work. Your manager must approve before the OT hours are counted in payroll.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input 
                        type="date"
                        value={otForm.date}
                        onChange={(e) => setOtForm({...otForm, date: e.target.value})}
                        data-testid="ot-date-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>OT Hours *</Label>
                        <Input 
                          type="number"
                          step="0.5"
                          min="0.5"
                          max="8"
                          value={otForm.requested_hours}
                          onChange={(e) => setOtForm({...otForm, requested_hours: e.target.value})}
                          placeholder="e.g., 2"
                          data-testid="ot-hours-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Project Code</Label>
                        <Input 
                          value={otForm.project_code}
                          onChange={(e) => setOtForm({...otForm, project_code: e.target.value})}
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Input 
                          type="time"
                          value={otForm.start_time}
                          onChange={(e) => setOtForm({...otForm, start_time: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Input 
                          type="time"
                          value={otForm.end_time}
                          onChange={(e) => setOtForm({...otForm, end_time: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Reason *</Label>
                      <Textarea 
                        value={otForm.reason}
                        onChange={(e) => setOtForm({...otForm, reason: e.target.value})}
                        placeholder="Why is overtime needed? (e.g., project deadline, urgent deliverable)"
                        data-testid="ot-reason-input"
                      />
                    </div>
                    <div className="bg-amber-50 text-amber-800 p-3 rounded-md text-sm flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>Overtime worked without pre-approval will appear in exception reports and may not be compensated.</span>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOtDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleOtSubmit} data-testid="submit-ot-btn">Submit Request</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Requested Hours</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Approved Hours</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {otRequests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {req.date}
                          {req.is_holiday_ot && (
                            <Badge variant="outline" className="text-amber-600 border-amber-600">Holiday</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{req.requested_hours}h</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {req.start_time && req.end_time ? `${req.start_time} - ${req.end_time}` : '-'}
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">{req.reason}</TableCell>
                      <TableCell>
                        {req.status === 'approved' ? (
                          <span className="text-green-600 font-medium">{req.approved_hours}h</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                    </TableRow>
                  ))}
                  {otRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No overtime requests. Request approval before working overtime.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyRequestsPage;
